import { useState, useEffect, useMemo, useCallback } from "react";
import { useTable } from "react-table";
import Cookies from "universal-cookie";
import { useNavigate } from "react-router";
import PageTemplate from "../../Templates/PageTemplate";
import VATModals from "../../Modals/VATModal";
import DefaultVATModal from "./DefaultVATModal"; // Import your new modal
import TablePreset from "../../Table/TablePreset";
import { BackButton } from "../../HouseStyle/Components/Buttons";
import middleware from "../../Api/Middleware";
import { RegularSearchBar } from "../../HouseStyle/Components/SearchBars";
import { formatPercentage, getCountryName, getAllCountries } from "../../../utils";
import { ButtonBlue } from "../../HouseStyle/Components/Buttons"
import ToastError from "../../Toasts/ToastError";
import ToastSuccess from "../../Toasts/ToastSuccess";

function VATSettings() {
    const cookies = new Cookies();
    const navigate = useNavigate();
    const [pagination, setPagination] = useState({
        page_size: 10,
        page: 1,
        max_items: 0,
        begin_of_page: 0,
        end_of_page: 0,
    });
    const [loading, setLoading] = useState(true);
    const [taxes, setTaxes] = useState([]);
    const [allTaxes, setAllTaxes] = useState([]);
    const [showTaxEdit, setShowTaxEdit] = useState(false);
    const [taxEditData, setTaxEditData] = useState([]);
    const [searchBar, setSearchBar] = useState("");
    const [low, setLow] = useState('');
    
    // Add state for default VAT modal
    const [showDefaultVATModal, setShowDefaultVATModal] = useState(false);
    const [defaultVATValues, setDefaultVATValues] = useState({
        product: '',
        shipping: '',
        payment: ''
    });
    const [backofficeSettings, setBackofficeSettings] = useState(null);

    function setLoadingScreenFunc() {
        setTimeout(() => {
            setLoading(false);
        }, 400);
    }

    // Add function to fetch backoffice settings
    const fetchBackofficeSettings = async () => {
        try {
            const response = await middleware.get('settings/backoffice');
            
            if (response.data.status === 'success' && response.data.data.length > 0) {
                const settings = response.data.data[0];
                setBackofficeSettings(settings);
                setDefaultVATValues({
                    product: settings.default_product_tax || '',
                    shipping: settings.default_shipping_tax || '',
                    payment: settings.default_payment_tax || ''
                });
            }
        } catch (error) {
            console.error('Error fetching backoffice settings:', error);
            ToastError('Er is een fout opgetreden bij het ophalen van de instellingen', 'fetch_settings_error');
        }
    };

    const fetchAll = async () => {
        try {
            // Fetch all tax rates without search
            const [fetchTax] = await Promise.all([
                middleware.get(
                    `sp/taxRatesTableAll?page=1&page_size=1000`
                ),
            ]);
            
            setAllTaxes(fetchTax.data.data || []);
            
            // Filter based on search term
            let filteredTaxes = fetchTax.data.data || [];
            
            if (searchBar.trim()) {
                const allCountries = getAllCountries('nl');
                const matchingCountries = allCountries.filter(country =>
                    country.name.toLowerCase().includes(searchBar.trim().toLowerCase()) ||
                    country.code.toLowerCase().includes(searchBar.trim().toLowerCase())
                );
                
                if (matchingCountries.length > 0) {
                    const matchingCodes = matchingCountries.map(c => c.code);
                    filteredTaxes = filteredTaxes.filter(tax =>
                        matchingCodes.includes(tax.country_code_2)
                    );
                } else {
                    filteredTaxes = [];
                }
            }
            
            setTaxes(filteredTaxes);
            setLow(filteredTaxes.length > 0 ? filteredTaxes[0].reduced_percentage : '');
            setPagination({
                ...pagination,
                max_items: filteredTaxes.length,
                begin_of_page: filteredTaxes.length > 0 ? 1 : 0,
                end_of_page: Math.min(filteredTaxes.length, pagination.page_size),
            });
            setLoadingScreenFunc();
        } catch (error) {
            console.error(error);
            if (error.response?.status === 401) {
                cookies.remove("token", { path: "/" });
                navigate("/");
            } else {
                ToastError('Er is een fout opgetreden bij het ophalen van de BTW gegevens', 'fetch_tax_error');
            }
        }
    };

    // Add useEffect to fetch backoffice settings when component mounts
    useEffect(() => {
        fetchBackofficeSettings();
    }, []);

    useEffect(() => {
        fetchAll();
    }, [searchBar, pagination.page_size]);

    useEffect(() => {
        // Only re-apply filtering when search changes
        if (allTaxes.length > 0) {
            let filteredTaxes = allTaxes;
            
            if (searchBar.trim()) {
                const allCountries = getAllCountries('nl');
                const matchingCountries = allCountries.filter(country =>
                    country.name.toLowerCase().includes(searchBar.trim().toLowerCase()) ||
                    country.code.toLowerCase().includes(searchBar.trim().toLowerCase())
                );
                
                if (matchingCountries.length > 0) {
                    const matchingCodes = matchingCountries.map(c => c.code);
                    filteredTaxes = allTaxes.filter(tax =>
                        matchingCodes.includes(tax.country_code_2)
                    );
                } else {
                    filteredTaxes = [];
                }
            }
            
            setTaxes(filteredTaxes);
            setPagination(prev => ({
                ...prev,
                page: 1,
                max_items: filteredTaxes.length,
                begin_of_page: filteredTaxes.length > 0 ? 1 : 0,
                end_of_page: Math.min(filteredTaxes.length, prev.page_size),
            }));
        }
    }, [searchBar, allTaxes]);

    const handleSearchChange = useCallback((value) => {
        setSearchBar(value);
        setPagination((prev) => ({ ...prev, page: 1 }));
    }, []);

    // Handle default VAT modal changes
    const handleDefaultVATChange = (field, value) => {
        setDefaultVATValues(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Handle default VAT modal save
    const handleDefaultVATSave = async () => {
        try {
            if (!backofficeSettings) {
                ToastError('Geen instellingen beschikbaar om op te slaan', 'no_settings_error');
                return;
            }

            // Validate that all fields have values
            if (!defaultVATValues.product || !defaultVATValues.shipping || !defaultVATValues.payment) {
                ToastError('Alle BTW instellingen moeten worden ingevuld', 'incomplete_vat_error');
                return;
            }

            // Prepare the payload with all required fields plus the updated tax settings
            const payload = {
                ...backofficeSettings,
                default_product_tax: defaultVATValues.product,
                default_shipping_tax: defaultVATValues.shipping,
                default_payment_tax: defaultVATValues.payment
            };
            
            const response = await middleware.put('settings/backoffice', payload);
            
            if (response.data.status === 'success') {
                setShowDefaultVATModal(false);
                ToastSuccess('Standaard BTW instellingen zijn succesvol opgeslagen', 'vat_save_success');
                
                // Refresh the settings after save
                await fetchBackofficeSettings();
            } else {
                ToastError('Er is een fout opgetreden bij het opslaan van de instellingen', 'vat_save_error');
            }
        } catch (error) {
            console.error('Error saving default VAT settings:', error);
            
            // Check for specific error messages from the server
            if (error.response?.data?.message) {
                ToastError(`Fout bij opslaan: ${error.response.data.message}`, 'vat_save_server_error');
            } else if (error.response?.status === 422) {
                ToastError('Ongeldige gegevens ingevoerd. Controleer alle velden.', 'vat_validation_error');
            } else if (error.response?.status === 403) {
                ToastError('U heeft geen rechten om deze instellingen te wijzigen', 'vat_permission_error');
            } else {
                ToastError('Er is een onbekende fout opgetreden bij het opslaan', 'vat_unknown_error');
            }
        }
    };

    // Handle opening the modal - fetch fresh data
    const handleOpenDefaultVATModal = async () => {
        try {
            await fetchBackofficeSettings();
            setShowDefaultVATModal(true);
        } catch (error) {
            console.error('Error opening modal:', error);
            ToastError('Er is een fout opgetreden bij het openen van de instellingen', 'modal_open_error');
        }
    };

    const columns = useMemo(() => {
        return [
            {
                Header: () => <p className="FlatText mb-0">Land</p>,
                accessor: "country",
                Cell: ({ row }) => {
                    return (
                        <div
                            className="text-start FlatTextLink"
                            onClick={() => {
                                setShowTaxEdit(true);
                                setTaxEditData(row.original);
                                setLow(row.original.reduced_percentage);
                            }}
                        >
                            {getCountryName(row.original.country_code_2)}
                        </div>
                    );
                },
            },
            {
                Header: () => <p className="FlatText mb-0">Hoog</p>,
                accessor: "high_percentage",
                Cell: ({ row }) => {
                    return (
                        <div className="text-start BlackText">
                            {formatPercentage(row.original.high_percentage)}
                        </div>
                    );
                },
            },
            {
                Header: () => <p className="FlatText mb-0">Laag</p>,
                accessor: "reduced",
                Cell: ({ row }) => {
                    const { selected_reduced } = row.original;
                    let selectedPercentage = null;
                    // Find the selected reduced percentage
                    if (selected_reduced === row.original.reduced_tax_id) {
                        selectedPercentage = row.original.reduced_percentage;
                    } else if (selected_reduced === row.original.reduced2_tax_id) {
                        selectedPercentage = row.original.reduced2_percentage;
                    } else if (selected_reduced === row.original.reduced3_tax_id) {
                        selectedPercentage = row.original.reduced3_percentage;
                    } else {
                        // Fallback: show first available reduced percentage
                        selectedPercentage = row.original.reduced_percentage ||
                            row.original.reduced2_percentage ||
                            row.original.reduced3_percentage;
                    }
                    return (
                        <div className="text-start BlackText">
                            {formatPercentage(selectedPercentage)}
                        </div>
                    );
                },
            },
            {
                Header: () => <p className="FlatText mb-0">Overig</p>,
                accessor: "zero_percentage",
                Cell: ({ row }) => {
                    return (
                        <div className="text-start BlackText">
                            {formatPercentage(row.original.zero_percentage)}
                        </div>
                    );
                },
            },
        ];
    }, []);

    // Table instance
    const tableInstance = useTable({ columns, data: taxes });

    return (
        <>
            <VATModals
                key={taxes.primary_key}
                taxEdit={showTaxEdit}
                setTaxEdit={(e) => setShowTaxEdit(e)}
                fetchAll={fetchAll}
                taxEditData={taxEditData}
                low={low}
                setLow={setLow}
            />
            
            {/* Add the DefaultVATModal */}
            <DefaultVATModal
                show={showDefaultVATModal}
                onHide={() => setShowDefaultVATModal(false)}
                values={defaultVATValues}
                onChange={handleDefaultVATChange}
                onSave={handleDefaultVATSave}
            />

            <PageTemplate navbarTitle={"BTW-instellingen"} pageIsLoading={""}>
                <div className="w-100">
                    <div className="mb-3">
                        <BackButton text={"Terug"} onClickFunction={() => navigate("/algemene/instellingen")} />
                    </div>
                    <div className="d-flex w-50">
                        <RegularSearchBar
                            placeholder={"Zoeken..."}
                            size="l"
                            searchFunction={handleSearchChange}
                        />
                        <div className="ms-3">
                            <ButtonBlue 
                                text={"Standaard BTW instellingen"} 
                                onClickFunction={handleOpenDefaultVATModal}
                            />
                        </div>
                    </div>
                    <TablePreset
                        data={[
                            {
                                tableInstance: tableInstance,
                                pagination: pagination,
                                primary_key: "primary_key",
                            },
                        ]}
                        handleSelection={() => { }}
                        tableLoading={loading}
                        setPagination={setPagination}
                        showcheckbox={false}
                    />
                </div>
            </PageTemplate>
        </>
    );
}

export default VATSettings;